import React, { useEffect, useRef } from "react";
import { FPS, KEYS, TRACK } from "./faceTrack";

/* Steffi's face rebuilt as continuous parameters rather than swapped poses.
 *
 * Everything lives in the same units the capture was measured in: 100 units is
 * half the source frame's height, the origin is the face centre. One rAF loop
 * writes SVG attributes directly, so React never re-renders while it plays.
 *
 *   mode "captured"   replays the 240-frame track lifted from the reference clip
 *   mode "procedural" generates speech-like motion of its own, endlessly
 */

const IX = {};
KEYS.forEach((k, i) => {
  IX[k] = i;
});

const lerp = (a, b, t) => a + (b - a) * t;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/* Sample the captured track at an arbitrary time, interpolating between the
   24 fps frames so playback is smooth at whatever the display refreshes at. */
function sampleTrack(seconds) {
  const pos = (seconds * FPS) % TRACK.length;
  const i = Math.floor(pos);
  const j = (i + 1) % TRACK.length;
  const t = pos - i;
  const a = TRACK[i];
  const b = TRACK[j];
  const out = new Array(a.length);
  for (let k = 0; k < a.length; k += 1) out[k] = lerp(a[k], b[k], t);
  return out;
}

/* A speech-like envelope with no audio behind it: syllables of random length,
   each with its own attack and release, plus a little jitter. Deterministic
   enough to look intentional, irregular enough not to read as a loop. */
function makeSpeaker() {
  let next = 0;
  let from = 0;
  let to = 0;
  let start = 0;
  let dur = 0.18;
  let open = 0;
  let wide = 0;

  return function speak(now) {
    if (now > next) {
      start = now;
      dur = 0.11 + Math.random() * 0.17;
      from = open;
      to = Math.random() < 0.22 ? 0.05 : 0.25 + Math.random() * 0.75;
      /* a breath every so often, so it is not a constant stream */
      next = now + dur + (Math.random() < 0.14 ? 0.35 + Math.random() * 0.4 : 0.02);
      wide = Math.random();
    }
    const t = clamp((now - start) / dur, 0, 1);
    /* fast in, slow out — the shape a jaw actually makes */
    const eased = t < 0.35 ? (t / 0.35) ** 0.6 : 1 - ((t - 0.35) / 0.65) ** 1.6;
    open = lerp(from, to, clamp(eased, 0, 1));
    return { open, wide };
  };
}

/* Endpoints plus the point the curve must pass through at its midpoint.
   For a quadratic that means the control point is 2M - (P0 + P2) / 2. */
function arc(x1, y1, x2, y2, mx, my) {
  const cx = 2 * mx - (x1 + x2) / 2;
  const cy = 2 * my - (y1 + y2) / 2;
  return `M ${x1.toFixed(2)} ${y1.toFixed(2)} Q ${cx.toFixed(2)} ${cy.toFixed(2)} ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

export default function TalkingFace({
  mode = "captured",
  speed = 1,
  playing = true,
  color = "#5fe9f5",
  glow = 5.5,
  className = "",
}) {
  const eyeL = useRef(null);
  const eyeR = useRef(null);
  const browL = useRef(null);
  const browR = useRef(null);
  const mouth = useRef(null);

  const cfg = useRef({ mode, speed, playing });
  cfg.current = { mode, speed, playing };

  useEffect(() => {
    let raf = 0;
    let clock = 0;
    let last = performance.now();
    const speak = makeSpeaker();

    /* procedural state, eased towards its targets every frame */
    const s = {
      open: 0,
      wide: 0.5,
      lid: 0,
      brow: 0,
      blinkAt: 2.2,
      blinkStart: -1,
    };

    const paintEye = (node, cx, cy, w, h, flat) => {
      if (!node) return;
      /* flat ~0.42 is a full ellipse, ~1.0 is a lid squared off at the top */
      const lidness = clamp((flat - 0.42) / 0.58, 0, 1);
      const r = lerp(Math.min(w, h) / 2, 3.2, lidness);
      node.setAttribute("x", (cx - w / 2).toFixed(2));
      node.setAttribute("y", (cy - h / 2).toFixed(2));
      node.setAttribute("width", w.toFixed(2));
      node.setAttribute("height", h.toFixed(2));
      node.setAttribute("rx", r.toFixed(2));
      node.setAttribute("ry", r.toFixed(2));
    };

    const paintMouth = (cx, half, top, bot, corner) => {
      if (!mouth.current) return;
      const x1 = cx - half;
      const x2 = cx + half;
      const upper = arc(x1, corner, x2, corner, cx, top);
      const lowerCx = 2 * cx - (x1 + x2) / 2;
      const lowerCy = 2 * bot - 2 * corner + corner;
      mouth.current.setAttribute(
        "d",
        `${upper} Q ${lowerCx.toFixed(2)} ${lowerCy.toFixed(2)} ${x1.toFixed(2)} ${corner.toFixed(2)} Z`
      );
    };

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const { mode: m, speed: sp, playing: on } = cfg.current;
      if (on) clock += dt * sp;

      if (m === "captured") {
        const v = sampleTrack(clock);
        paintEye(eyeL.current, v[IX.eLx], v[IX.eLy], v[IX.eLw], v[IX.eLh], v[IX.eLflat]);
        paintEye(eyeR.current, v[IX.eRx], v[IX.eRy], v[IX.eRw], v[IX.eRh], v[IX.eRflat]);
        browL.current?.setAttribute(
          "d",
          arc(v[IX.bLx1], v[IX.bLy1], v[IX.bLx2], v[IX.bLy2],
              (v[IX.bLx1] + v[IX.bLx2]) / 2, (v[IX.bLy1] + v[IX.bLy2]) / 2 + v[IX.bLsag])
        );
        browR.current?.setAttribute(
          "d",
          arc(v[IX.bRx1], v[IX.bRy1], v[IX.bRx2], v[IX.bRy2],
              (v[IX.bRx1] + v[IX.bRx2]) / 2, (v[IX.bRy1] + v[IX.bRy2]) / 2 + v[IX.bRsag])
        );
        const browOpacity = clamp(v[IX.brow], 0, 1).toFixed(2);
        browL.current?.setAttribute("opacity", browOpacity);
        browR.current?.setAttribute("opacity", browOpacity);
        paintMouth(v[IX.mx], v[IX.mhalf], v[IX.mtop], v[IX.mbot], v[IX.mcorner]);
        return;
      }

      /* ---------------------------------------------------- procedural mode */
      const sig = speak(clock);
      s.open = lerp(s.open, sig.open, 0.35);
      s.wide = lerp(s.wide, sig.wide, 0.08);

      /* the lids close a little as the mouth opens, which reads as effort */
      s.lid = lerp(s.lid, 0.18 + s.open * 0.4, 0.12);
      s.brow = lerp(s.brow, 0.5 + Math.sin(clock * 0.7) * 0.3 + s.open * 0.25, 0.06);

      if (s.blinkStart < 0 && clock > s.blinkAt) s.blinkStart = clock;
      let blink = 1;
      if (s.blinkStart >= 0) {
        const t = (clock - s.blinkStart) * 1000;
        if (t < 90) blink = 1 - t / 90;
        else if (t < 240) blink = (t - 90) / 150;
        else {
          s.blinkStart = -1;
          s.blinkAt = clock + 2.6 + Math.random() * 3.4;
        }
      }

      const eyeH = lerp(37, 24, s.lid) * clamp(blink, 0.06, 1);
      const eyeW = 28;
      const flat = 0.42 + s.lid * 0.5;
      paintEye(eyeL.current, -39.4, -22.5, eyeW, eyeH, flat);
      paintEye(eyeR.current, 39.4, -22.5, eyeW, eyeH, flat);

      const by = -46 - s.brow * 4;
      const tilt = (s.brow - 0.5) * 3;
      browL.current?.setAttribute("d", arc(-53, by + tilt, -26, by - tilt, -39.5, by - 3.4));
      browR.current?.setAttribute("d", arc(26, by - tilt, 53, by + tilt, 39.5, by - 3.4));
      browL.current?.setAttribute("opacity", "1");
      browR.current?.setAttribute("opacity", "1");

      const half = lerp(15, 34, 0.35 + s.wide * 0.4 + s.open * 0.35);
      const corner = 26.5;
      const openAmt = s.open * 22;
      paintMouth(0, half, corner - openAmt * 0.42, corner + openAmt * 0.58, corner);
    };

    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <svg
      viewBox="-100 -100 200 200"
      className={className}
      style={{ display: "block", width: "100%", height: "100%", overflow: "visible" }}
      aria-hidden="true"
    >
      <defs>
        <filter id="tf-glow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation={glow} result="halo" />
          <feColorMatrix
            in="halo"
            type="matrix"
            values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.9 0"
            result="soft"
          />
          <feMerge>
            <feMergeNode in="soft" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <g filter="url(#tf-glow)">
        <path ref={browL} fill="none" stroke={color} strokeWidth="4.6" strokeLinecap="round" />
        <path ref={browR} fill="none" stroke={color} strokeWidth="4.6" strokeLinecap="round" />
        <rect ref={eyeL} fill={color} />
        <rect ref={eyeR} fill={color} />
        <path
          ref={mouth}
          fill="none"
          stroke={color}
          strokeWidth="4.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}
